import { type ReactElement, useEffect, useMemo, useState } from "react";

import { getApiSourceInfo } from "../../api.js";
import {
  dailyChartAxisLabel,
  dailyChartAxisStyle,
  dailyChartRotateAxisLabels,
} from "../../lib/dashboardDayLabel.js";
import type {
  DailyUptimeRow,
  FilterState,
  HealthDeviceDailyUptime,
  HealthDeviceOption,
  UptimeTimelineSegment,
} from "../../types.js";
import { ChartCard } from "../ChartCard.js";

/** Match index.css --color-ok / --color-warn; gap is darker than --color-err. */
const UPTIME_FILL = "#31c48d";
const SCHEDULED_FILL = "#f9c74f";
const GAP_FILL = "#7a3548";
const BAR_TRACK_FILL = "#15253f";

function defaultDeviceId(devices: HealthDeviceOption[]): string {
  if (!devices.length) return "";
  const table1 = devices.find((d) => d.name.trim().toLowerCase() === "table 1");
  if (table1) return table1.id;
  return devices[0]!.id;
}

function fillForKind(kind: UptimeTimelineSegment["kind"]): string {
  if (kind === "unmeasured") return "#6b7280";
  if (kind === "scheduled") return SCHEDULED_FILL;
  if (kind === "up") return UPTIME_FILL;
  return GAP_FILL;
}

function DailyUptimeBars({ data }: { data: DailyUptimeRow[] }) {
  if (!data.length) {
    return <p className="py-5 text-[13px] text-text-secondary">No uptime data yet</p>;
  }

  const shown = data.slice(-14);
  const rotateLabels = dailyChartRotateAxisLabels(shown.length);
  const width = 620;
  const axisReserve = rotateLabels ? 52 : 28;
  const legendTop = 32;
  const valueLabelBand = 20;
  const plotTop = legendTop + valueLabelBand;
  const height = 260 + (rotateLabels ? 28 : 0) + valueLabelBand;
  const chartLeft = 44;
  const chartBottom = axisReserve;
  const chartHeight = height - plotTop - chartBottom;
  const innerWidth = width - chartLeft - 8;
  const barGap = 6;
  const maxBarWidth = 52;
  const barWidth = Math.min(
    maxBarWidth,
    Math.max(10, Math.floor((innerWidth - barGap * (shown.length - 1)) / shown.length)),
  );
  const barsStartX = chartLeft;
  const axisHours = Math.max(24, ...shown.map((row) => row.capacityHours));
  const yForHours = (hours: number) =>
    plotTop + (1 - Math.min(axisHours, Math.max(0, hours)) / axisHours) * chartHeight;
  const svgCssHeight = 240 + (rotateLabels ? 28 : 0) + valueLabelBand;

  function hoursLabel(x: number, hoursAboveBaseline: number, text: string) {
    const barTopY = yForHours(hoursAboveBaseline);
    const labelY = Math.max(legendTop + 12, barTopY - 6);
    return (
      <text x={x + barWidth / 2} y={labelY} textAnchor="middle" fill="#dce6ff" fontSize="10">
        {text}
      </text>
    );
  }

  function UptimeRibbon({ row, x }: { row: DailyUptimeRow; x: number }) {
    const capHoursSafe = Math.max(0.000_001, row.capacityHours);
    const yCapTop = yForHours(row.capacityHours);
    const yCapBot = yForHours(0);
    const fullPx = Math.max(0, yCapBot - yCapTop);

    const denomMs = Math.max(1, row.capacityMs || Math.round(capHoursSafe * 3600000));

    const ordered = row.timeline;

    const up = Math.max(0, row.uptimeHours);
    const sched = Math.max(0, row.scheduledHours);
    const unsched = Math.max(0, row.unscheduledHours);
    const total = up + sched + unsched + row.unmeasuredHours;

    const ribbonRects: ReactElement[] = [];
    ordered.forEach((seg, si) => {
      const durMs = Math.max(0, seg.endMsInDay - seg.startMsInDay);
      const h = fullPx <= 0 || denomMs <= 0 ? 0 : (durMs / denomMs) * fullPx;
      if (h <= 0) return;
      const yTop = yCapBot - (seg.endMsInDay / denomMs) * fullPx;
      ribbonRects.push(
        <rect
          key={`${seg.startMsInDay}-${seg.endMsInDay}-${si}`}
          x={x}
          y={yTop}
          width={barWidth}
          height={h}
          fill={fillForKind(seg.kind)}
        >
          <title>{seg.tooltip.replaceAll("\n", " — ")}</title>
        </rect>,
      );
    });

    return (
      <>
        <rect x={x} y={yCapTop} width={barWidth} height={fullPx} fill={BAR_TRACK_FILL} />
        <g>{ribbonRects}</g>
        {hoursLabel(x, row.capacityHours, total.toFixed(1))}
      </>
    );
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={svgCssHeight}>
      <g>
        <rect x={chartLeft} y={12} width={10} height={10} fill={UPTIME_FILL} />
        <text x={58} y={21} fill="#9eb2d7" fontSize="11">
          Uptime
        </text>
        <rect x={116} y={12} width={10} height={10} fill={SCHEDULED_FILL} />
        <text x={132} y={21} fill="#9eb2d7" fontSize="11">
          Scheduled
        </text>
        <rect x={196} y={12} width={10} height={10} fill={GAP_FILL} />
        <text x={212} y={21} fill="#9eb2d7" fontSize="11">
          No signal
        </text>
        <rect x={300} y={12} width={10} height={10} fill="#6b7280" />
        <text x={316} y={21} fill="#9eb2d7" fontSize="11">
          Not measured
        </text>
      </g>

      <line
        x1={chartLeft}
        y1={yForHours(axisHours)}
        x2={width - 8}
        y2={yForHours(axisHours)}
        stroke="#385074"
        strokeWidth="1"
      />
      <line
        x1={chartLeft}
        y1={yForHours(12)}
        x2={width - 8}
        y2={yForHours(12)}
        stroke="#324868"
        strokeDasharray="3 3"
        strokeWidth="1"
      />
      <line
        x1={chartLeft}
        y1={yForHours(0)}
        x2={width - 8}
        y2={yForHours(0)}
        stroke="#385074"
        strokeWidth="1"
      />
      <text x={6} y={yForHours(axisHours) + 4} fill="#9eb2d7" fontSize="11">
        {axisHours}h
      </text>
      <text x={8} y={yForHours(12) + 4} fill="#9eb2d7" fontSize="11">
        12h
      </text>
      <text x={14} y={yForHours(0) + 4} fill="#9eb2d7" fontSize="11">
        0h
      </text>

      {shown.map((row, i) => {
        const x = barsStartX + i * (barWidth + barGap);
        const axisStyle = dailyChartAxisStyle(row.day);
        const axisY = rotateLabels ? height - axisReserve + 4 : height - 8;
        return (
          <g key={row.day}>
            <UptimeRibbon row={row} x={x} />
            <text
              x={x + barWidth / 2}
              y={axisY}
              textAnchor={rotateLabels ? "end" : "middle"}
              dominantBaseline={rotateLabels ? "middle" : undefined}
              fontSize={10}
              fontWeight={axisStyle.axisLabelFontWeight}
              fill={axisStyle.axisLabelFill}
              transform={rotateLabels ? `rotate(-90 ${x + barWidth / 2} ${axisY})` : undefined}
            >
              {dailyChartAxisLabel(row.day)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const UPTIME_CRITERIA = `One named table in the venue/device scope. Each UTC minute in device_heartbeat_minutes means the platform received an authenticated content or kiosk heartbeat. Consecutive minutes are merged in SQL, then clipped to DST-aware reporting days and elapsed capacity today. No interpolation across missing minutes.

Current venue schedules (or declared downtime brackets when unconfigured) override overlapping heartbeat minutes. Red = no heartbeat received outside those windows. Grey = historical time without a confirmed heartbeat or declared scheduled window. Device heat and games pulses do not determine uptime.

Bar positions are chronological, starting at reporting midnight. These are server rollups, so Raw Events drill-down is unavailable.`;

interface UptimeBreakdownPerDayCardProps {
  dailyUptimeByDevice: HealthDeviceDailyUptime[];
  devices: HealthDeviceOption[];
  filters: FilterState;
  /** When false, hides the ⓘ criteria toggle (Overview / PDF). */
  showCriteria?: boolean;
}

export function UptimeBreakdownPerDayCard({
  dailyUptimeByDevice,
  devices,
  filters,
  showCriteria = true,
}: UptimeBreakdownPerDayCardProps) {
  const apiSource = getApiSourceInfo();
  const sortedDevices = useMemo(
    () =>
      [...devices].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
      ),
    [devices],
  );
  const filterLocksSingle =
    filters.deviceId !== "all" && sortedDevices.some((d) => d.id === filters.deviceId);
  const showDropdown = !filterLocksSingle && sortedDevices.length > 1;

  const [selectedDeviceId, setSelectedDeviceId] = useState(() =>
    filterLocksSingle ? filters.deviceId : defaultDeviceId(sortedDevices),
  );

  useEffect(() => {
    if (filterLocksSingle) {
      setSelectedDeviceId(filters.deviceId);
      return;
    }
    setSelectedDeviceId((prev) => {
      if (prev && sortedDevices.some((d) => d.id === prev)) return prev;
      return defaultDeviceId(sortedDevices);
    });
  }, [filterLocksSingle, filters.deviceId, sortedDevices]);

  const selectedRows = useMemo(() => {
    const match = dailyUptimeByDevice.find((d) => d.deviceId === selectedDeviceId);
    return match?.dailyUptime ?? [];
  }, [dailyUptimeByDevice, selectedDeviceId]);

  const selectedName = (() => {
    const d = sortedDevices.find((x) => x.id === selectedDeviceId);
    if (!d) {
      return dailyUptimeByDevice.find((x) => x.deviceId === selectedDeviceId)?.deviceName ?? "";
    }
    const venue = d.venueName?.trim();
    return venue ? `${d.name} · ${venue}` : d.name;
  })();

  return (
    <ChartCard
      title="Single Table Uptime Breakdown"
      criteria={showCriteria ? UPTIME_CRITERIA : undefined}
      titleRight={
        showDropdown ? (
          <label className="flex items-center gap-1.5 text-[11px] text-text-secondary">
            <span className="sr-only">Table</span>
            <select
              className="min-w-[220px] max-w-[min(100%,320px)] rounded-md border border-border-subtle bg-panel-2 px-2 py-1 text-[12px] font-medium text-text-primary"
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              aria-label="Table"
            >
              {sortedDevices.map((d) => {
                const venue = d.venueName?.trim();
                const label = venue ? `${d.name} · ${venue}` : d.name;
                return (
                  <option key={d.id} value={d.id}>
                    {label}
                  </option>
                );
              })}
            </select>
          </label>
        ) : selectedName ? (
          <span className="text-[12px] font-medium text-text-secondary">{selectedName}</span>
        ) : null
      }
      note={`Chronological bar (bottom = start of reporting day): yellow = scheduled, green = up, dark red = no signal, grey = not measured. API: ${apiSource.label}.`}
    >
      <DailyUptimeBars data={selectedRows} />
    </ChartCard>
  );
}
